import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge, Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';

interface TenantRow {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

interface BuildingResource {
  id: string;
  unifi_resource_type: 'door' | 'door_group' | 'device';
  unifi_resource_id: string;
  name: string;
  full_name: string | null;
  last_seen_at: string;
  tenant_grants: Array<{
    id: string;
    tenant_id: string;
    label_override: string | null;
    enabled: boolean;
    tenant: { id: string; name: string };
  }>;
}

interface AccessZone {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  resources: Array<{
    building_resource_id: string;
    building_resource: BuildingResource;
  }>;
  tenant_assignments: Array<{
    tenant_id: string;
    tenant: { id: string; name: string; status?: TenantRow['status'] };
  }>;
}

async function loadAll() {
  const [resources, tenants, zones] = await Promise.all([
    apiClient.get<{ data: BuildingResource[] }>('/api/v1/admin/hardware/resources', {
      skipTenantScope: true,
    }),
    apiClient.get<{ data: TenantRow[] }>('/api/v1/tenants', { skipTenantScope: true }),
    apiClient.get<{ data: AccessZone[] }>('/api/v1/admin/access-zones', {
      skipTenantScope: true,
    }),
  ]);
  return { resources: resources.data, tenants: tenants.data, zones: zones.data };
}

export function HardwareClaimPage() {
  const [resources, setResources] = useState<BuildingResource[] | null>(null);
  const [zones, setZones] = useState<AccessZone[] | null>(null);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // UX-013: the grant POST minted its key inside the submit handler, so a
  // retry after a failure — the dialog stays open — looked like a second
  // grant. The key belongs to the open dialog, which is the intent.
  const [modal, setModal] = useState<{
    resource: BuildingResource;
    tenantId: string;
    label: string;
    idempotencyKey: string;
  } | null>(null);
  const [zoneModal, setZoneModal] = useState<{
    name: string;
    description: string;
    resourceIds: string[];
  } | null>(null);
  const [assignModal, setAssignModal] = useState<{ zone: AccessZone; tenantId: string } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const activeTenants = useMemo(() => tenants.filter((t) => t.status === 'active'), [tenants]);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await loadAll();
      setResources(data.resources);
      setTenants(data.tenants);
      setZones(data.zones);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load door permissions');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function refreshInventory() {
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(
        '/api/v1/admin/hardware/resources/refresh',
        {},
        { skipTenantScope: true },
      );
      setToast('Inventory refreshed from UniFi.');
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to refresh inventory');
    } finally {
      setBusy(false);
    }
  }

  async function grant(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!modal) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.withIdempotencyKey(modal.idempotencyKey).post(
        '/api/v1/admin/hardware/grants',
        {
          tenant_id: modal.tenantId,
          building_resource_id: modal.resource.id,
          label_override: modal.label.trim() || null,
        },
        { skipTenantScope: true },
      );
      setToast(`Granted ${modal.resource.name}.`);
      setModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to grant resource');
    } finally {
      setBusy(false);
    }
  }

  async function createZone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!zoneModal) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(
        '/api/v1/admin/access-zones',
        {
          name: zoneModal.name.trim(),
          description: zoneModal.description.trim() || null,
          resource_ids: zoneModal.resourceIds,
        },
        { skipTenantScope: true },
      );
      setToast(`Created ${zoneModal.name.trim()}.`);
      setZoneModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create access zone');
    } finally {
      setBusy(false);
    }
  }

  async function assignZone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!assignModal) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(
        `/api/v1/admin/access-zones/${assignModal.zone.id}/tenants`,
        { tenant_id: assignModal.tenantId },
        { skipTenantScope: true },
      );
      setToast(`Assigned ${assignModal.zone.name}.`);
      setAssignModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to assign access zone');
    } finally {
      setBusy(false);
    }
  }

  async function removeZoneAssignment(zone: AccessZone, tenantId: string) {
    setBusy(true);
    setError(null);
    try {
      await apiClient.delete(`/api/v1/admin/access-zones/${zone.id}/tenants/${tenantId}`, {
        skipTenantScope: true,
      });
      setToast(`Removed ${zone.name}.`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove access zone');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(grantId: string) {
    setBusy(true);
    setError(null);
    try {
      await apiClient.delete(`/api/v1/admin/hardware/grants/${grantId}`, {
        skipTenantScope: true,
      });
      setToast('Grant revoked.');
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to revoke grant');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">Access Zones</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Group building-owned doors into reusable zones, then assign zones to tenants.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setZoneModal({ name: '', description: '', resourceIds: [] })}
          >
            <Icon name="add" className="text-sm mr-1" />
            Create zone
          </Button>
          <Button onClick={refreshInventory} loading={busy}>
            <Icon name="refresh" className="text-sm mr-1" />
            Refresh inventory
          </Button>
        </div>
      </header>

      {toast ? (
        <div
          role="status"
          className="rounded-lg bg-secondary-container text-on-secondary-container px-4 py-2"
        >
          {toast}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="rounded-lg bg-error-container text-on-error-container px-4 py-3"
        >
          {error}
        </div>
      ) : null}

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {resources == null || zones == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : zones.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant">
            No access zones yet. Create a zone like Common Areas after refreshing inventory.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-container">
              <tr>
                <Th>Zone</Th>
                <Th>Doors</Th>
                <Th>Assigned tenants</Th>
                <Th align="right">&nbsp;</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {zones.map((zone) => (
                <tr key={zone.id} className="hover:bg-surface-container-low">
                  <td className="px-4 py-3">
                    <div className="font-body-md text-body-md text-on-surface">{zone.name}</div>
                    {zone.description ? (
                      <div className="font-body-sm text-body-sm text-on-surface-variant">
                        {zone.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {zone.resources.length === 0 ? (
                        <span className="text-on-surface-variant">No doors</span>
                      ) : (
                        zone.resources.map((resource) => (
                          <Badge key={resource.building_resource_id} tone="neutral">
                            {resource.building_resource.full_name ??
                              resource.building_resource.name}
                          </Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {zone.tenant_assignments.length === 0 ? (
                        <span className="text-on-surface-variant">No tenants</span>
                      ) : (
                        zone.tenant_assignments.map((assignment) => (
                          <span
                            key={assignment.tenant_id}
                            className="inline-flex items-center gap-2 rounded-full bg-surface-container px-3 py-1 text-sm"
                          >
                            {assignment.tenant.name}
                            <button
                              type="button"
                              className="text-error hover:underline"
                              onClick={() => void removeZoneAssignment(zone, assignment.tenant_id)}
                              disabled={busy}
                            >
                              Remove
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="secondary"
                      disabled={activeTenants.length === 0}
                      onClick={() => setAssignModal({ zone, tenantId: activeTenants[0]?.id ?? '' })}
                    >
                      <Icon name="group_add" className="text-sm mr-1" />
                      Assign
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-surface-container">
          <h2 className="font-title-md text-title-md text-on-surface">Advanced Grants</h2>
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            Use individual grants only for exceptions that do not fit an Access Zone.
          </p>
        </div>
        {resources == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : resources.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant">
            No building resources yet. Refresh inventory after adding doors/readers in UniFi Access.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-container">
              <tr>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>UniFi ID</Th>
                <Th>Granted tenants</Th>
                <Th align="right">&nbsp;</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {resources.map((resource) => (
                <tr key={resource.id} className="hover:bg-surface-container-low">
                  <td className="px-4 py-3">
                    <div className="font-body-md text-body-md text-on-surface">
                      {resource.full_name ?? resource.name}
                    </div>
                    <div className="font-body-sm text-body-sm text-on-surface-variant">
                      Seen {new Date(resource.last_seen_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={resource.unifi_resource_type === 'door' ? 'success' : 'neutral'}>
                      {resource.unifi_resource_type.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-code text-code">{resource.unifi_resource_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {resource.tenant_grants.filter((g) => g.enabled).length === 0 ? (
                        <span className="text-on-surface-variant">No tenants</span>
                      ) : (
                        resource.tenant_grants
                          .filter((g) => g.enabled)
                          .map((grant) => (
                            <span
                              key={grant.id}
                              className="inline-flex items-center gap-2 rounded-full bg-surface-container px-3 py-1 text-sm"
                            >
                              {grant.tenant.name}
                              <button
                                type="button"
                                className="text-error hover:underline"
                                onClick={() => void revoke(grant.id)}
                                disabled={busy}
                              >
                                Revoke
                              </button>
                            </span>
                          ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setModal({
                          resource,
                          tenantId: activeTenants[0]?.id ?? '',
                          label: '',
                          idempotencyKey: newIdempotencyKey(),
                        })
                      }
                      disabled={activeTenants.length === 0}
                    >
                      <Icon name="add" className="text-sm mr-1" />
                      Grant
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {zoneModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile">
          <form
            onSubmit={createZone}
            className="w-full max-w-[560px] rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-lg space-y-4"
          >
            <h2 className="font-headline-sm text-headline-sm text-on-surface">
              Create Access Zone
            </h2>
            <label className="block space-y-2">
              <span className="font-label-md text-label-md">Zone name</span>
              <input
                required
                value={zoneModal.name}
                onChange={(e) => setZoneModal((m) => (m ? { ...m, name: e.target.value } : m))}
                className="w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2"
                placeholder="Common Areas"
              />
            </label>
            <label className="block space-y-2">
              <span className="font-label-md text-label-md">Description</span>
              <input
                value={zoneModal.description}
                onChange={(e) =>
                  setZoneModal((m) => (m ? { ...m, description: e.target.value } : m))
                }
                className="w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2"
                placeholder="Shared entries, lobby, elevators"
              />
            </label>
            <fieldset className="space-y-2">
              <legend className="font-label-md text-label-md">Doors</legend>
              <div className="max-h-64 overflow-auto rounded-lg border border-outline-variant divide-y divide-outline-variant">
                {(resources ?? [])
                  .filter((resource) => resource.unifi_resource_type === 'door')
                  .map((resource) => {
                    const label = resource.full_name ?? resource.name;
                    const checked = zoneModal.resourceIds.includes(resource.id);
                    return (
                      <label key={resource.id} className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={checked}
                          onChange={(e) =>
                            setZoneModal((m) => {
                              if (!m) return m;
                              return {
                                ...m,
                                resourceIds: e.target.checked
                                  ? [...m.resourceIds, resource.id]
                                  : m.resourceIds.filter((id) => id !== resource.id),
                              };
                            })
                          }
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
              </div>
            </fieldset>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setZoneModal(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" loading={busy}>
                Create zone
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      {assignModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile">
          <form
            onSubmit={assignZone}
            className="w-full max-w-[460px] rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-lg space-y-4"
          >
            <h2 className="font-headline-sm text-headline-sm text-on-surface">
              Assign {assignModal.zone.name}
            </h2>
            <label className="block space-y-2">
              <span className="font-label-md text-label-md">Tenant</span>
              <select
                required
                value={assignModal.tenantId}
                onChange={(e) =>
                  setAssignModal((m) => (m ? { ...m, tenantId: e.target.value } : m))
                }
                className="w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2"
              >
                {activeTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAssignModal(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" loading={busy}>
                Assign
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile">
          <form
            onSubmit={grant}
            className="w-full max-w-[460px] rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-lg space-y-4"
          >
            <h2 className="font-headline-sm text-headline-sm text-on-surface">
              Grant {modal.resource.name}
            </h2>
            <label className="block space-y-2">
              <span className="font-label-md text-label-md">Tenant</span>
              <select
                required
                value={modal.tenantId}
                onChange={(e) => setModal((m) => (m ? { ...m, tenantId: e.target.value } : m))}
                className="w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2"
              >
                {activeTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-2">
              <span className="font-label-md text-label-md">Tenant label</span>
              <input
                value={modal.label}
                onChange={(e) => setModal((m) => (m ? { ...m, label: e.target.value } : m))}
                className="w-full rounded-lg border border-outline bg-surface-container-lowest px-3 py-2"
                placeholder={modal.resource.full_name ?? modal.resource.name}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setModal(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" loading={busy}>
                Grant
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3`}
    >
      {children}
    </th>
  );
}
